import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  Dialog,
  IconButton,
  Typography,
  Divider,
  Button,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import HistoryIcon from '@mui/icons-material/History';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { tokens } from '../theme/theme';
import api from '../api';

/** Bearer headers for same-origin or absolute API file URLs (iframes cannot send Authorization). */
function getAuthHeadersForMedia() {
  const jwtToken = localStorage.getItem('access_token') || localStorage.getItem('jwtToken');
  if (!jwtToken) return {};
  return { Authorization: `Bearer ${jwtToken}` };
}

function extOf(name) {
  return (name || '').split('.').pop().toLowerCase();
}

function fmtSize(b) {
  if (b == null || b === '') return '—';
  const n = Number(b);
  if (Number.isNaN(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

export default function PreviewDrawer({
  open,
  onClose,
  file,
  fullScreen = false,
  folderPathLabel,
  versionCount,
  sharedWithClient,
  onDownload,
  onShare,
  onDelete,
  onOpenVersionHistory,
  /** Client Files: open full-screen coordinate picker (create e-sign draft + place fields). PDF only. */
  onPlaceSignatureFields,
}) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const drawerWidth = 640;
  const [versionHint, setVersionHint] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const url = file?.file_url || file?.url;
  const name = file?.file_name || 'File';
  const ext = extOf(name);

  const kind = useMemo(() => {
    if (ext === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (['xlsx', 'xls', 'docx', 'doc', 'pptx', 'ppt'].includes(ext)) return 'office';
    return 'other';
  }, [ext]);

  useEffect(() => {
    if (!open || kind !== 'pdf' || !url) {
      setPdfBlobUrl((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return null;
      });
      setPdfLoading(false);
      setPdfError(null);
      return undefined;
    }

    let cancelled = false;
    setPdfLoading(true);
    setPdfError(null);

    (async () => {
      try {
        const res = await fetch(url, { headers: getAuthHeadersForMedia() });
        if (!res.ok) {
          throw new Error(`Could not load PDF (${res.status})`);
        }
        const blob = await res.blob();
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl((prev) => {
          if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
          return blobUrl;
        });
      } catch (e) {
        if (!cancelled) {
          setPdfError(e instanceof Error ? e.message : 'Preview failed');
          setPdfBlobUrl((prev) => {
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return null;
          });
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, kind, url, file?.id]);

  useEffect(() => {
    if (versionCount != null) {
      setVersionHint(String(versionCount));
      return;
    }
    setVersionHint(null);
    if (!file?.id || String(file.id).startsWith('dr_')) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/files/${file.id}/versions/`);
        const list = res.data?.versions || res.data || [];
        if (!cancelled) setVersionHint(Array.isArray(list) ? String(list.length) : '—');
      } catch {
        if (!cancelled) setVersionHint('—');
      }
    })();
    return () => { cancelled = true; };
  }, [file?.id, versionCount]);

  const previewBody = (
    <Box sx={{ flex: 1, minHeight: 200, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      {kind === 'pdf' && url && (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
          {pdfLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Loading preview…
            </Typography>
          )}
          {!pdfLoading && pdfError && (
            <Typography variant="body2" color="error.main" sx={{ textAlign: 'center', px: 1 }}>
              {pdfError}
            </Typography>
          )}
          {!pdfLoading && pdfBlobUrl && (
            <Box
              component="iframe"
              title={name}
              src={`${pdfBlobUrl}#toolbar=1`}
              sx={{ width: '100%', height: fullScreen ? 'calc(100vh - 200px)' : 480, border: 0, borderRadius: 1, bgcolor: 'background.paper' }}
            />
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon fontSize="small" />}
            onClick={() => {
              if (pdfBlobUrl) {
                window.open(pdfBlobUrl, '_blank', 'noopener,noreferrer');
              } else {
                window.open(url, '_blank', 'noopener,noreferrer');
              }
            }}
            sx={{ textTransform: 'none', alignSelf: 'center' }}
          >
            Open PDF in new tab
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', px: 1 }}>
            Preview loads securely using your session. If a tab still fails to open, use Download in the header.
          </Typography>
        </Box>
      )}
      {kind === 'image' && url && (
        <Box
          component="img"
          src={url}
          alt=""
          sx={{ maxWidth: '100%', maxHeight: fullScreen ? '70vh' : 400, objectFit: 'contain', borderRadius: 1 }}
        />
      )}
      {kind === 'image' && !url && (
        <Typography color="text.secondary">No preview URL for this image</Typography>
      )}
      {kind === 'office' && (
        <Box sx={{ textAlign: 'center', maxWidth: 360 }}>
          <InsertDriveFileOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Preview not available — download to view this file type.
          </Typography>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => onDownload?.(file)} sx={{ textTransform: 'none' }}>
            Download
          </Button>
        </Box>
      )}
      {kind === 'other' && (
        <Box sx={{ textAlign: 'center', maxWidth: 360 }}>
          <InsertDriveFileOutlinedIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {name}
          </Typography>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => onDownload?.(file)} sx={{ textTransform: 'none' }}>
            Download
          </Button>
        </Box>
      )}
      {kind === 'pdf' && !url && (
        <Typography color="text.secondary">No preview URL</Typography>
      )}
    </Box>
  );

  const metaPanel = (
    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'text.primary' }}>
        Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Uploaded by: <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{file?.uploaded_by || '—'}</Box> on {fmtDateTime(file?.uploaded_at)}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Size: <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{fmtSize(file?.file_size)}</Box>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Folder: <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{folderPathLabel || 'All files'}</Box>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Shared with client:{' '}
        <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{sharedWithClient ? 'Yes' : 'No'}</Box>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Last modified: {fmtDateTime(file?.uploaded_at)}
      </Typography>
      <Button
        size="small"
        startIcon={<HistoryIcon fontSize="small" />}
        onClick={() => onOpenVersionHistory?.(file)}
        sx={{ textTransform: 'none', mt: 0.5, p: 0, minWidth: 0 }}
        disabled={String(file?.id || '').startsWith('dr_')}
      >
        Versions: {versionHint ?? '…'}
      </Button>
      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Comments / notes for this file are not wired to an API yet (TODO: backend file notes).
      </Typography>
    </Box>
  );

  const header = (
    <Toolbar
      sx={{
        gap: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minHeight: 56,
        pr: 1,
      }}
    >
      <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700, minWidth: 0 }} noWrap>
        {name}
      </Typography>
      <IconButton aria-label="Download file" edge="end" onClick={() => onDownload?.(file)} size="small">
        <DownloadIcon fontSize="small" />
      </IconButton>
      <IconButton aria-label="Share with client" edge="end" onClick={() => onShare?.(file)} size="small">
        <ShareIcon fontSize="small" />
      </IconButton>
      <IconButton aria-label="Delete file" edge="end" onClick={() => onDelete?.(file)} size="small" sx={{ color: 'error.main' }}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
      <IconButton aria-label="Close preview" edge="end" onClick={onClose} size="small">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Toolbar>
  );

  const shell = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: fullScreen ? '100%' : '100%', bgcolor: 'background.paper' }}>
      {header}
      {onPlaceSignatureFields && kind === 'pdf' && (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <Button
            variant="contained"
            fullWidth
            size="medium"
            startIcon={<BorderColorOutlinedIcon />}
            onClick={() => onPlaceSignatureFields()}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Select signature coordinates
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.4 }}>
            Tap here to place Signature 1, Signature 2, … on this PDF, then send for signing. Same as the row menu → E-signature.
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto' }}>
        {previewBody}
        {metaPanel}
      </Box>
    </Box>
  );

  if (fullScreen) {
    return (
      <Dialog fullScreen open={open} onClose={onClose} PaperProps={{ sx: { bgcolor: 'background.default' } }}>
        {shell}
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: isXs ? '100%' : drawerWidth,
          maxWidth: '100%',
          boxShadow: tokens.shadow.lg,
        },
      }}
    >
      {shell}
    </Drawer>
  );
}
